import { tccf7Logger } from './tccf7ErrorHandler.js';
import { TCCF7_CONFIG } from './tccf7Constants.js';

/*-------------------------------------------------------------------------------

  確認画面の作成

--------------------------------------------------------------------------------*/

export default class tccf7Create {

  /*----------------------------------------
    HTMLエスケープ
  ------------------------------------------*/
  escapeHTML(str) {
    return str.replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
  }

  /*----------------------------------------
    labelからテキスト取得
  ------------------------------------------*/
  getLabelText(input) {
    if( !input.checked ) return '';
    const label = input.closest('label');
    let labelText = '';
    if (label) {
      const span = label.querySelector(TCCF7_CONFIG.SELECTORS.LABEL);
      labelText = span
        ? span.textContent.trim()
        : input.value;
    } else {
      labelText = input.value;
    }
    return labelText;
  }

  /*----------------------------------------
    入力画面：コピーを作成
  ------------------------------------------*/
  copyInput(inputWrap) {
    if( !inputWrap ) return;
    // cloneNode(true) はユーザーが動的に変更した select.value を保持しないため事前に保存する
    const selectValues = Array.from(inputWrap.querySelectorAll('select')).map(s => s.value);
    const cloneInputWrap = inputWrap.cloneNode(true);
    cloneInputWrap.removeAttribute('id');
    cloneInputWrap.classList.remove(TCCF7_CONFIG.CLASSNAME.NONE);
    // クローン後にselectの選択状態を復元
    cloneInputWrap.querySelectorAll('select').forEach((s, i) => {
      s.value = selectValues[i];
    });
    return cloneInputWrap;
  }

  /*----------------------------------------
    確認画面：作成：DOMが無い時
  ------------------------------------------*/
  createConfirm(confirmWrapId, inputWrap, form) {
    let confirmWrapElem = document.getElementById(confirmWrapId);
    if (confirmWrapElem) return confirmWrapElem;
    confirmWrapElem = document.createElement('div');
    confirmWrapElem.id = confirmWrapId;
    const targetReference = inputWrap ? inputWrap.nextSibling : null;
    form.insertBefore(confirmWrapElem, targetReference);
    return confirmWrapElem;
  }
  
  /*----------------------------------------
    確認画面：表：作成
  ------------------------------------------*/
  createConfirmTable(inputWrap, confirmWrap) {
    try { 
      if (!inputWrap || !confirmWrap) throw new Error('Required elements missing');

      const inputs = inputWrap.querySelectorAll('input, textarea, select');
      if (inputs.length === 0) return;

      const checkInput = ['textarea', 'select'];
      let counter = 0;
      let value = '';

      inputs.forEach(input => {
        const tagName = input.tagName.toLowerCase();
        const type = tagName === 'select' ? 'select' : (input.type || tagName);
        if (['submit', 'hidden', 'button'].includes(type)) return;

        const isCheckInput = checkInput.includes(type);
        const typeElem     = isCheckInput ? type : `input[type="${type}"]`;

        let name = input.getAttribute('name') || '';
            name = name.replace(/\[\]$/, '');

        const data        = name ? `[data-name="${name}"]` : false;
        const dataElem    = data ? inputWrap.querySelector(data) || input : input;

        const groupInputs = dataElem ? dataElem.querySelectorAll(typeElem) : [];
        const length      = groupInputs.length;

        const selected    = input.getAttribute('data-selected-value');
        const accept      = input.closest(TCCF7_CONFIG.SELECTORS.ACCEPTANCE);

        if (length > 1 && counter < length) {
          counter++;
        }

        let shouldReplace = true;

        if (accept && input.checked) {
          value = input.nextSibling ? input.nextSibling.textContent : '承諾済み';
        } else if (type === 'radio' || type === 'checkbox') {
          shouldReplace = false;
          value += this.getLabelText(input);
        } else if (type === 'file') {
          input.classList.add(TCCF7_CONFIG.CLASSNAME.NONE);
          this.createFile(input, dataElem);
          counter = 0;
          value = '';
          return;
        } else if (selected) {
          value = selected;
        } else {
          value = input.value;
        }

        if (dataElem && (shouldReplace || counter === length || length <= 1)) {
          const span = document.createElement('span');
          span.classList.add('tccf7-confirm-value');
          
          const output = value.trim() === '' ? '未入力' : value.trim();
          const emptyClass = value.trim() === '' ? 'tccf7-confirm-empty' : '';
          if (emptyClass) span.classList.add(emptyClass);
          span.innerHTML = this.escapeHTML(output).replace(/\n/g, '<br>');
          
          dataElem.replaceWith(span);
          counter = 0;
          value = '';
        }

      });

      confirmWrap.replaceChildren(inputWrap);

    } catch (error) {
      tccf7Logger.log('createConfirmTable', error);
      this.createBasicConfirmView(confirmWrap);
    }
  }

  /*----------------------------------------
    画像プレビュー生成
  ------------------------------------------*/
  createFile(input, dataElem) {
    if (!input || !dataElem) return;
    
    try {
      if (input.files.length === 0) {
        const span = document.createElement('span');
        span.textContent = '選択なし';
        dataElem.appendChild(span);
        return;
      }

      Array.from(input.files).forEach(file => {
        try {
          const wrapper = document.createElement('div');
          wrapper.classList.add('tccf7-file-preview');

          // 画像の場合
          if (file.type.startsWith('image/')) {
            const img = document.createElement('img');
            const objectUrl = URL.createObjectURL(file);
            
            img.src = objectUrl;
            img.style.maxWidth = '500px';
            img.style.display = 'block'; // レイアウト崩れ防止

            img.onload = () => URL.revokeObjectURL(objectUrl);
            
            img.onerror = () => {
              // ここで span を新しく作る必要があります
              const errSpan = document.createElement('span');
              tccf7Logger.log('Image Load', `Failed to load: ${file.name}`, 'warn');
              URL.revokeObjectURL(objectUrl);
              
              errSpan.textContent = `${file.name} - 表示エラー（ファイルは選択されています）`;
              errSpan.style.color = '#d63638';
              errSpan.style.fontSize = '0.9em';
              
              // 壊れた画像アイコンを表示させないためにimgを消してメッセージに置換
              img.replaceWith(errSpan);
            };
            
            wrapper.appendChild(img);

          } else {
            // 画像以外の場合（PDFなど）
            const span = document.createElement('span');
            // サイズを表示してあげるとユーザーが安心します
            const fileSize = (file.size / 1024).toFixed(1);
            span.textContent = `📎 ${file.name} (${fileSize} KB)`;
            wrapper.appendChild(span);
          }

          dataElem.appendChild(wrapper);

        } catch (innerError) {
          // catch の引数名を innerError にして外側と区別
          tccf7Logger.log('createFile:IndividualFile', innerError, 'warn');
          const errorSpan = document.createElement('span');
          errorSpan.textContent = `⚠️ ${file.name} 処理中にエラー`;
          dataElem.appendChild(errorSpan);
        }
      });
    } catch (error) {
      tccf7Logger.log('createFile', error);
    }
  }
  
  /*----------------------------------------
    送信完了画面：作成：リダイレクト無の場合用
  ------------------------------------------*/
  createComplete(completeId, confirmWrap, success) {
    const complete = document.getElementById(completeId);
    if (complete) {
      complete.remove();
    }
    const completeWrap = document.createElement('div');
    completeWrap.id = completeId;
    completeWrap.className = `tccf7-complete-msg ${success ? 'is-success' : 'is-error'}`;
    completeWrap.setAttribute('role', 'status');
    completeWrap.setAttribute('aria-live', 'polite');
    const fragment = document.createDocumentFragment();
    const completeTitle = document.createElement('p');
    completeTitle.className = 'complete-ttl';
    completeTitle.textContent = success ? '送信完了' : '送信失敗';
    fragment.appendChild(completeTitle);
    const completeText = document.createElement('p');
    completeText.className = 'complete-txt';
    const msgSuccess = 'フォームの送信が完了しました。自動返信メールが届かない場合は、迷惑メールフォルダに振り分けられていないかご確認ください。';
    const msgError = 'フォームの送信がうまくいきませんでした。内容に不備がないか確認し、再度送信いただくか、時間をおいてお試しください。';
    completeText.textContent = success ? msgSuccess : msgError;
    fragment.appendChild(completeText);
    completeWrap.appendChild(fragment);
    confirmWrap.after(completeWrap);
    return completeWrap;
  }
}